import type {
  SlackGetUsersResponse,
  SlackSendCreditNoteNotificationRequestPayload,
  SlackSendCreditNoteNotificationResponse,
  SlackSendInvoiceApprovalMessageRequestPayload,
  SlackSendInvoiceApprovalMessageResponse,
} from '@vy/api-definition';
import { SlackGetUsersResponseSchema } from '@vy/api-definition';
import { INTEGRATION_PROVIDERS } from '@vy/types';
import { duration, withDefault } from '@vy/utils';
import type { UseMutationOptions } from 'react-query';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { getPrivateApi } from '../../services/api';
import { queryKeys } from '../../services/query-key/query-keys';
import { useActivatedIntegration } from '../use-activated-integration';
import type { CustomUseQueryOptions } from './requests-types/custom-use-query-options';

export function useSlackGetUsers(options?: CustomUseQueryOptions<SlackGetUsersResponse>) {
  const isSlackActivated = useActivatedIntegration(INTEGRATION_PROVIDERS.slack, { suspense: false });
  const isEnabledInOptions = withDefault(options?.enabled, true);
  const enabled = isEnabledInOptions && isSlackActivated;
  const staleTime = duration(1, 'hours').asMilliseconds();
  const cacheTime = duration(2, 'hours').asMilliseconds();

  return useQuery(
    queryKeys.slackUsers(),
    async () => {
      return getSlackUsers();
    },
    {
      ...options,
      enabled,
      staleTime,
      cacheTime,
    },
  );
}

async function getSlackUsers() {
  try {
    const privateApi = getPrivateApi();
    const slackUsers = await privateApi.getSlackUsers();

    return SlackGetUsersResponseSchema.parse(slackUsers);
  } catch (error) {
    console.error('Error fetching Slack users:', error);

    return { users: [] };
  }
}

export function useSlackAuthRedirect() {
  const queryClient = useQueryClient();

  return useMutation(async () => {
    const api = getPrivateApi();
    const redirectResponse = await api.slackConnect({
      redirectUrl: window.location.href,
    });

    await queryClient.invalidateQueries(queryKeys.connectedIntegrations());
    window.location.href = redirectResponse.redirectUrl;
  });
}

export function useShareInvoiceToSlack(
  options?: UseMutationOptions<
    SlackSendInvoiceApprovalMessageResponse,
    unknown,
    SlackSendInvoiceApprovalMessageRequestPayload
  >,
) {
  const api = getPrivateApi();

  return useMutation<
    SlackSendInvoiceApprovalMessageResponse,
    unknown,
    SlackSendInvoiceApprovalMessageRequestPayload
  >(async ({ userId, invoice }) => {
    const response = await api.sendSlackInvoiceApprovalMessage({
      slackSendInvoiceApprovalMessageRequestSchema: { userId, invoice },
    });

    return response;
  }, options);
}

export function useShareCreditNoteToSlack(
  options?: UseMutationOptions<
    SlackSendCreditNoteNotificationResponse,
    unknown,
    SlackSendCreditNoteNotificationRequestPayload
  >,
) {
  const api = getPrivateApi();

  return useMutation<
    SlackSendCreditNoteNotificationResponse,
    unknown,
    SlackSendCreditNoteNotificationRequestPayload
  >(async ({ userId, creditLedgerEntryId }) => {
    const response = await api.sendSlackCreditNoteNotification({
      slackSendCreditNoteNotificationRequest: { userId, creditLedgerEntryId },
    });

    return response;
  }, options);
}
